import { TherapyTarget } from 'domains/signup/data/types';
import { TherapyConcerns } from 'domains/signupv4/Mini-Screener/miniScreenerMachine';
import i18next from 'i18next';
import { uniq } from 'lodash';
import { TFunction } from 'i18next';

export type ClinicalArea =
  | 'Adult Receptive-Expressive Language Disorder'
  | 'Pediatric Articulation/Phonological Disorder'
  | 'Pediatric/Adolescent Social-Pragmatics'
  | 'Gender Affirming Voice'
  | 'Augmentative and Alternative Communication (AAC)'
  | 'Acquired Apraxia'
  | 'Adult Articulation'
  | 'Aphasia'
  | 'Adult Fluency/Stuttering'
  | 'Pediatric Dysphagia/Sensory Feeding'
  | 'Voice'
  | 'Parkinsons'
  | 'Early Language Development'
  | 'Pediatric Fluency/Stuttering'
  | 'Autism Spectrum Disorder'
  | 'Public/Professional Speaking'
  | 'Pediatric Receptive-Expressive Language Disorder'
  | 'Accent Modification'
  | 'Adult Cognition'
  | 'Other';

export type PediatricConcern =
  | 'Pronouncing sounds clearly'
  | 'Using words to communicate'
  | 'Understanding others'
  | 'Speech or language delay'
  | 'Stuttering'
  | 'Lisp'
  | 'Autism'
  | 'Socializing with others'
  | 'Feeding or swallowing'
  | 'Assisted communication device'
  | 'Other';

export type AdultConcern =
  | 'Expressing thoughts clearly'
  | 'Clarity of speech'
  | 'Understanding language'
  | 'Stuttering'
  | 'Voice clarity'
  | 'Voice alignment with gender identity'
  | 'Concentration and memory'
  | 'Acquired apraxia'
  | 'Neurodegenerative disorder'
  | 'Post-stroke'
  | 'Post-traumatic brain injury'
  | 'Professional communication skills'
  | 'Accent modification'
  | 'Using a device or speech app for communication'
  | 'Other';

export interface PediatricConcernOption {
  key: PediatricConcern;
  clinicalAreas: ClinicalArea[];
  allowEval: boolean;
  allowAnySLP: boolean;
}

export interface AdultConcernOption {
  key: AdultConcern;
  clinicalAreas: ClinicalArea[];
  allowEval: boolean;
  allowAnySLP: boolean;
}

export const SIGNUP_CONCERNS_PEDIATRIC: PediatricConcernOption[] = [
  {
    key: 'Pronouncing sounds clearly',
    clinicalAreas: ['Pediatric Articulation/Phonological Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Using words to communicate',
    clinicalAreas: ['Early Language Development', 'Pediatric Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Understanding others',
    clinicalAreas: ['Pediatric Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Speech or language delay',
    clinicalAreas: [
      'Early Language Development',
      'Pediatric Receptive-Expressive Language Disorder',
      'Pediatric Articulation/Phonological Disorder',
    ],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Stuttering',
    clinicalAreas: ['Pediatric Fluency/Stuttering'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Lisp',
    clinicalAreas: ['Pediatric Articulation/Phonological Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Autism',
    clinicalAreas: ['Autism Spectrum Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Socializing with others',
    clinicalAreas: ['Pediatric/Adolescent Social-Pragmatics'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Feeding or swallowing',
    clinicalAreas: ['Pediatric Dysphagia/Sensory Feeding'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Assisted communication device',
    clinicalAreas: ['Augmentative and Alternative Communication (AAC)'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Other',
    clinicalAreas: ['Other'],
    allowEval: false,
    allowAnySLP: true,
  },
];

export const SIGNUP_CONCERNS_ADULT: AdultConcernOption[] = [
  {
    key: 'Expressing thoughts clearly',
    clinicalAreas: ['Adult Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Clarity of speech',
    clinicalAreas: ['Adult Articulation'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Understanding language',
    clinicalAreas: ['Adult Receptive-Expressive Language Disorder'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Stuttering',
    clinicalAreas: ['Adult Fluency/Stuttering'],
    allowEval: true,
    allowAnySLP: true,
  },
  {
    key: 'Voice clarity',
    clinicalAreas: ['Voice'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Voice alignment with gender identity',
    clinicalAreas: ['Gender Affirming Voice'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Concentration and memory',
    clinicalAreas: ['Adult Cognition'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Acquired apraxia',
    clinicalAreas: ['Acquired Apraxia'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Neurodegenerative disorder',
    clinicalAreas: ['Parkinsons'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Post-stroke',
    clinicalAreas: ['Aphasia'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Post-traumatic brain injury',
    clinicalAreas: ['Aphasia'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Professional communication skills',
    clinicalAreas: ['Public/Professional Speaking'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Accent modification',
    clinicalAreas: ['Accent Modification'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Using a device or speech app for communication',
    clinicalAreas: ['Augmentative and Alternative Communication (AAC)'],
    allowEval: true,
    allowAnySLP: false,
  },
  {
    key: 'Other',
    clinicalAreas: ['Other'],
    allowEval: false,
    allowAnySLP: false,
  },
];

export const getCommunicationConcernOptions = ({
  currentKeys,
  therapyTarget,
}: {
  currentKeys?: string[];
  therapyTarget?: TherapyTarget;
}) => {
  if (!therapyTarget || !currentKeys) return undefined;

  if (therapyTarget === 'child') {
    return SIGNUP_CONCERNS_PEDIATRIC.filter(v => currentKeys.includes(v.key));
  }

  return SIGNUP_CONCERNS_ADULT.filter(v => currentKeys.includes(v.key));
};

export const getSpecialtyOptionsPediatric = (t: TFunction) =>
  SIGNUP_CONCERNS_PEDIATRIC.map(value => ({
    label: t(`miniScreener.steps.enterConcerns.concerns.${value.key}`),
    value: value.key,
  }));

export const getSpecialtyOptionsAdult = (t: TFunction) =>
  SIGNUP_CONCERNS_ADULT.map(value => ({
    label: t(`miniScreener.steps.enterConcerns.concerns.${value.key}`),
    value: value.key,
  }));

export const COMMUNICATION_CONCERNS_TPREFIX = {
  child: 'miniScreener.steps.enterConcerns.pediatric',
  adult: 'miniScreener.steps.enterConcerns.adult',
};

export const OPTION_OTHER = 'Other';

export const getClinicalAreas = (fullConcerns: TherapyConcerns['fullConcerns']) => {
  const categories = fullConcerns
    ?.filter(concern => concern.key !== OPTION_OTHER)
    .map(concern => concern.clinicalAreas)
    .flat();

  return uniq(categories);
};

/**
 * This function is used to calculate detected areas without filtering out the null values and "other concern" option
 * @param concerns
 * @param target
 * @returns detectedAreas as an array of DevelopmentalArea or null
 */
export const getClinicalAreasRaw = (fullConcerns: TherapyConcerns['fullConcerns']) => {
  const categories = fullConcerns?.map(concern => concern.clinicalAreas).flat();

  return uniq(categories);
};

export const mergeCommunicationConcerns = ({ extraInformation, otherConcerns, concerns }: TherapyConcerns) => {
  const concernIndexes = concerns.filter(concern => concern !== OPTION_OTHER);

  let communicationConcerns = '';

  concernIndexes.forEach(
    index =>
      (communicationConcerns += `Concern: ${i18next.t(`miniScreener.steps.enterConcerns.concerns.${index}`)} \n`),
  );

  if (otherConcerns) {
    communicationConcerns += `Other communication concerns: ${otherConcerns} \n`;
  }

  if (extraInformation) {
    communicationConcerns += `Extra information: ${extraInformation}`;
  }

  return communicationConcerns;
};
